/* ══════════════════════════════════════════════════════════════════════════
 * Logo canvas engine — port of the standalone generator's proxy3d.js
 *
 * Framework-agnostic Canvas2D drawing for the studietur logo:
 *   - AI city skyline image composited with luminosity blend
 *   - School name (auto-wrap + auto-shrink)
 *   - Student names rendered along an arc
 *
 * Both text elements use the face the customer picks in the
 * "Skrifttype til navne" selector — see logoFonts.js for the option table.
 *
 * ══════════════════════════════════════════════════════════════════════════ */

import { getNameFont, DEFAULT_NAME_FONT } from './logoFonts';

/* ── Canvas size ──────────────────────────────────────────────────────────
 * Height is trimmed to the composition: the top margin above the name ring is
 * ~105 and the lowest ink is the school line's descenders at ~1909, leaving a
 * comparable ~91 of clear space below.
 *
 * Every vertical anchor below is ABSOLUTE, never derived from canvas.height —
 * otherwise changing the height would slide the ring off the artwork.
 * ──────────────────────────────────────────────────────────────────────── */
export const CANVAS_WIDTH = 1800;
export const CANVAS_HEIGHT = 2000;

/* ── Artwork sizing ("Logo størrelse") ────────────────────────────────────
 * The slider value is the artwork width as a percentage of canvas width.
 * The artwork is scaled around its original centre (900, 880) so it stays
 * concentric with the student-name ring at every value. 70% reproduces the
 * historic fixed 1280px-wide artwork at y=240.
 *
 * The ring's inner diameter is 1540px (radius 770 × 2), so values above
 * ~85% let the artwork overlap the names — same as the reference build.
 * ──────────────────────────────────────────────────────────────────────── */
export const MIN_LOGO_SIZE = 50;
export const MAX_LOGO_SIZE = 95;
export const DEFAULT_LOGO_SIZE = 70;
export const LOGO_SIZE_STEP = 5;

const ARTWORK_CENTER_Y = 880; // = the original y:240 + 1280/2

/** Base sizes, tuned against Kaushan; scaled per face via `font.scale`. */
const SCHOOL_BASE_FONT_SIZE = 136;
const STUDENTS_BASE_FONT_SIZE = 80;

/** Floor for the auto-shrink loops — a non-positive size breaks `ctx.font`. */
const MIN_AUTO_FONT_SIZE = 8;

/** Active face, set from `options` on every drawLogo/init3D call. */
const activeFont = {
  stack: getNameFont(DEFAULT_NAME_FONT).stack,
  scale: getNameFont(DEFAULT_NAME_FONT).scale,
};

/** Canvas2D font shorthand for the active face. */
const fontString = (size) => `normal ${size}px ${activeFont.stack}`;

const schoolBaseFontSize = () => Math.round(SCHOOL_BASE_FONT_SIZE * activeFont.scale);
const studentsBaseFontSize = () => Math.round(STUDENTS_BASE_FONT_SIZE * activeFont.scale);

function applyFontOptions({ fontStack, fontScale } = {}) {
  if (fontStack) {
    activeFont.stack = fontStack;
  }
  if (typeof fontScale === 'number' && fontScale > 0) {
    activeFont.scale = fontScale;
  }
  schoolData.currentFontSize = schoolBaseFontSize();
  studentsData.currentFontSize = studentsBaseFontSize();
}

function clampLogoSize(value) {
  const size = typeof value === 'number' && Number.isFinite(value) ? value : DEFAULT_LOGO_SIZE;
  return Math.min(MAX_LOGO_SIZE, Math.max(MIN_LOGO_SIZE, size));
}

/**
 * Artwork rect for the given size percentage. Rounded to whole pixels —
 * drawBlackWhiteImage feeds it straight to getImageData/putImageData.
 */
function getArtworkRect(canvas, logoSize) {
  const width = Math.round(canvas.width * (clampLogoSize(logoSize) / 100));
  return {
    x: Math.round(canvas.width / 2 - width / 2),
    y: Math.round(ARTWORK_CENTER_Y - width / 2),
    width,
  };
}

const schoolData = {
  y: 1820,
  align: 'center',
  baseline: 'middle',
  currentFontSize: SCHOOL_BASE_FONT_SIZE,
};

/* ── School line ──────────────────────────────────────────────────────────
 * Class and school share ONE row — "2G • Roskilde Gymnasium". It never wraps;
 * if the line is too wide the font shrinks until it fits.
 * ──────────────────────────────────────────────────────────────────────── */

/** Clear space kept either side of the school line. */
const SCHOOL_SIDE_MARGIN = 50;

const SCHOOL_SEPARATOR = ' • ';

/** `"2G • Roskilde Gymnasium"`, skipping whichever part is missing. */
export function buildSchoolLabel(schoolName, className) {
  return [className, schoolName]
    .map((part) => (part || '').trim())
    .filter(Boolean)
    .join(SCHOOL_SEPARATOR);
}

/** Centre of the student-name ring — absolute, like ARTWORK_CENTER_Y. */
const RING_CENTER_Y = 900;

const studentsData = {
  radius: 770,

  currentFontSize: 0,
  /** Angle occupied by the names, gaps included. Used to centre on 12 o'clock. */
  offsetAngle: 0,
  /** Extra angle inserted between names to spread them around the ring. */
  gapAngle: 0,

  offsetFontSize: 2,
};

/* ── Name ring layout ─────────────────────────────────────────────────────
 * The names read as a ring AROUND the logotype at every list length, with the
 * leftover space gathered into one clean break at 6 o'clock.
 *
 * Space BETWEEN NAMES is the only lever that fills the ring — letter-spacing
 * is deliberately never stretched (that is what made the reference build look
 * spacy). A single name has no gaps to give, so it is left alone.
 * ──────────────────────────────────────────────────────────────────────── */

/** Names span at most this fraction of the circle; the rest is the bottom break. */
const MAX_SPAN = 0.85;

/**
 * Smallest gap between two names, as a fraction of the font size. A plain space
 * glyph is only ~0.22em in these script faces, so once a long list fills the
 * ring the names run into each other. Reserving this much in the fit test makes
 * the shrink loop trade font size for separation instead — which is what starts
 * to matter from roughly 16 names up.
 */
const MIN_NAME_GAP_EM = 0.6;

/**
 * How far a list of N names is pushed from its natural width toward MAX_SPAN.
 * 0 = leave it alone, 1 = fill to MAX_SPAN. Saturates at 12 names. Deliberately
 * smooth — there must be no visible jump when one more name is added.
 */
const FILL_RAMP = [0, 0, 0.25, 0.35, 0.45, 0.55, 0.65, 0.72, 0.8, 0.85, 0.9, 0.95, 1];

/** Font multiplier by name count — short lists read larger so they aren't lost on the ring. */
const GROWTH_RAMP = [1, 1, 1.3, 1.3, 1.25, 1.2, 1.13, 1.07, 1];

const rampValue = (ramp, count) => ramp[Math.min(count, ramp.length - 1)];

const bwsetup = {
  choosed: 2,
};

export function setBW(canvas, choosed) {
  if (choosed === 2) {
    const context = canvas.getContext('2d');
    context.save();
    context.globalCompositeOperation = 'luminosity';
    const test = context.globalCompositeOperation !== 'luminosity';
    context.restore();
    if (test) {
      return false;
    }
  }
  bwsetup.choosed = choosed;
  return true;
}

export function init3D(canvas, brand, options = {}) {
  applyFontOptions(options);
  drawSchoolText(canvas, 'SCHOOL', true);
  drawStudentsText(canvas, ['students'], true);
  drawBrand(canvas, brand);
}

function drawBrand(canvas, template) {
  if (!template) {
    return;
  }
  const context = canvas.getContext('2d');
  context.save();
  context.drawImage(template, 0, 0, canvas.width, canvas.height);
  context.restore();
}

/**
 * @param {object} [options]
 * @param {string} [options.fontStack] Canvas2D font-family stack for both text elements
 * @param {number} [options.fontScale] Per-face size multiplier (see logoFonts.js)
 * @param {number} [options.logoSize]  Artwork width as a % of canvas width (50–95)
 */
export function drawLogo(canvas, template, image, schoolName, className, students, options = {}) {
  applyFontOptions(options);
  const logoSize = clampLogoSize(options.logoSize);

  drawCoverImage(canvas);
  drawImage(canvas, image, logoSize);
  if (bwsetup.choosed === 1) {
    drawBlackWhiteImage(canvas, image, logoSize);
  }

  // Class and school share one row: "2G • Roskilde Gymnasium".
  const schoolLabel = buildSchoolLabel(schoolName, className);
  recalculateSchoolText(canvas, schoolLabel);
  drawSchoolText(canvas, schoolLabel);

  drawStudentsText(canvas, students);
}

function drawCoverImage(canvas) {
  const context = canvas.getContext('2d');
  context.save();
  context.rect(0, 0, canvas.width, canvas.height);
  context.fillStyle = 'white';
  context.fill();
  context.restore();
}

function drawImage(canvas, image, logoSize) {
  if (!image) {
    return;
  }
  const context = canvas.getContext('2d');
  context.save();
  const { x, y, width } = getArtworkRect(canvas, logoSize);
  if (bwsetup.choosed === 2) {
    context.globalCompositeOperation = 'luminosity';
  }
  context.drawImage(image, x, y, width, width);
  context.restore();
}

function drawBlackWhiteImage(canvas, image, logoSize) {
  if (!image) {
    return;
  }
  const context = canvas.getContext('2d');
  context.save();
  const { x, y, width } = getArtworkRect(canvas, logoSize);
  const imageData = context.getImageData(x, y, width, width);
  const data = imageData.data;
  for (let i = 0; i < data.length; i += 4) {
    const count = data[i] + data[i + 1] + data[i + 2];
    let colour = 0;
    if (count > 360) {
      colour = 255;
    }
    data[i] = colour; // red
    data[i + 1] = colour; // green
    data[i + 2] = colour; // blue
  }
  context.putImageData(imageData, x, y);
  context.restore();
}

/**
 * Painted width of `label`, not just its advance width — the script faces
 * overhang their advance with swashes, which would otherwise spill past the
 * margin. Falls back to the advance where the ink metrics aren't available.
 *
 * Only the SUM of the two bounding-box values is used: it is sign-convention
 * independent, unlike their difference, which node-canvas and browsers disagree
 * about. The line stays centred on its advance width, as the browser does it.
 */
function schoolTextWidth(context, label) {
  const m = context.measureText(label);
  const left = Number.isFinite(m.actualBoundingBoxLeft) ? Math.abs(m.actualBoundingBoxLeft) : 0;
  const right = Number.isFinite(m.actualBoundingBoxRight) ? Math.abs(m.actualBoundingBoxRight) : 0;
  return Math.max(m.width, left + right);
}

/**
 * Fits the school line to a single row. Font metrics are near-linear in size, so
 * jump straight to the estimated size and then correct a pixel at a time.
 */
function recalculateSchoolText(canvas, label) {
  schoolData.currentFontSize = schoolBaseFontSize();
  if (!label) {
    return;
  }

  const context = canvas.getContext('2d');
  const maxWidth = canvas.width - SCHOOL_SIDE_MARGIN * 2;
  context.save();
  context.textAlign = schoolData.align;
  context.textBaseline = schoolData.baseline;

  const widthAt = (size) => {
    context.font = fontString(size);
    return schoolTextWidth(context, label);
  };

  if (widthAt(schoolData.currentFontSize) > maxWidth) {
    const estimate = (schoolData.currentFontSize * maxWidth) / widthAt(schoolData.currentFontSize);
    schoolData.currentFontSize = Math.max(MIN_AUTO_FONT_SIZE, Math.floor(estimate));

    while (
      schoolData.currentFontSize > MIN_AUTO_FONT_SIZE &&
      widthAt(schoolData.currentFontSize) > maxWidth
    ) {
      schoolData.currentFontSize -= 1;
    }
  }

  context.restore();
}

function drawSchoolText(canvas, label, isPrepare = false) {
  if (!label) {
    return;
  }
  const context = canvas.getContext('2d');
  context.save();
  if (isPrepare) {
    context.fillStyle = 'white';
  }
  context.font = fontString(schoolData.currentFontSize);
  context.textAlign = schoolData.align;
  context.textBaseline = schoolData.baseline;
  context.fillText(label, canvas.width / 2, schoolData.y);
  context.restore();
}

function drawStudentsText(canvas, students, isPrepare = false) {
  const cleaned = [];
  for (const student of students) {
    const st = student.trim();
    if (st) {
      cleaned.push(st);
    }
  }
  if (cleaned.length === 0) {
    return;
  }
  const context = canvas.getContext('2d');
  context.save();
  context.textBaseline = schoolData.baseline;
  context.textAlign = schoolData.align;
  if (isPrepare) {
    context.fillStyle = 'white';
  }

  const centerX = canvas.width / 2;
  const centerY = RING_CENTER_Y;
  recalculateStudentsText(context, cleaned, studentsData.radius);

  drawTextAlongArc(context, cleaned, centerX, centerY, studentsData.radius);
  context.restore();
}

/**
 * Solves the ring layout for `names`, writing the result into `studentsData`:
 * font size, the gap inserted between names, and the total angle they occupy.
 */
function recalculateStudentsText(context, names, radius) {
  const count = names.length;
  studentsData.gapAngle = 0;
  studentsData.currentFontSize = Math.round(studentsBaseFontSize() * rampValue(GROWTH_RAMP, count));

  const maxAngle = MAX_SPAN * 2 * Math.PI;

  // Too many names to fit the ring — shrink until they do.
  for (let i = 0; i < 40; i++) {
    studentsData.offsetAngle = getStudentsEndAngle(context, names, radius);
    if (studentsData.offsetAngle <= maxAngle) {
      break;
    }
    // 40 steps of 2px would take the base 80px size to 0 on a very long name
    // list; a non-positive size makes the `ctx.font` assignment a silent no-op.
    if (studentsData.currentFontSize - studentsData.offsetFontSize < MIN_AUTO_FONT_SIZE) {
      break;
    }
    studentsData.currentFontSize -= studentsData.offsetFontSize;
  }

  // A single name has no gaps to give — leave it centred at the top as it is.
  if (count < 2) {
    return;
  }

  const naturalAngle = studentsData.offsetAngle;
  if (naturalAngle >= maxAngle) {
    return;
  }

  // Spread the names out toward MAX_SPAN, all of the slack going into the gaps.
  const targetAngle = naturalAngle + (maxAngle - naturalAngle) * rampValue(FILL_RAMP, count);
  studentsData.gapAngle = (targetAngle - naturalAngle) / (count - 1);
  studentsData.offsetAngle = targetAngle;
}

/**
 * Angle between two names: never less than MIN_NAME_GAP_EM, plus whatever extra
 * `gapAngle` the spread step added. Assumes the caller has already set the font.
 */
function nameGapAngle(context, radius) {
  const minGap = Math.max(
    context.measureText(' ').width,
    MIN_NAME_GAP_EM * studentsData.currentFontSize
  );
  return minGap / radius + studentsData.gapAngle;
}

/** Total angle `names` occupy at the current font size, gaps between them included. */
function getStudentsEndAngle(context, names, radius) {
  context.save();
  context.font = fontString(studentsData.currentFontSize);

  let finalAngle = 0;
  for (let n = 0; n < names.length; n++) {
    if (n > 0) {
      finalAngle += nameGapAngle(context, radius);
    }
    const name = names[n];
    for (let i = 0; i < name.length; i++) {
      finalAngle += context.measureText(name[i]).width / radius;
    }
  }
  context.restore();
  return finalAngle;
}

function drawTextAlongArc(context, names, centerX, centerY, radius) {
  context.save();
  context.font = fontString(studentsData.currentFontSize);

  context.translate(centerX, centerY);
  // Centre the whole run on 12 o'clock, so the leftover break lands at 6 o'clock.
  context.rotate(studentsData.offsetAngle * -0.5);

  for (let n = 0; n < names.length; n++) {
    if (n > 0) {
      context.rotate(nameGapAngle(context, radius));
    }

    const name = names[n];
    for (let i = 0; i < name.length; i++) {
      const letter = name[i];
      // Advance half a glyph, draw on the letter's own centre, advance the rest.
      const letterAngle = 0.5 * (context.measureText(letter).width / radius);
      context.rotate(letterAngle);
      context.save();

      context.translate(0, -radius);
      context.fillText(letter, 0, 0);
      context.restore();

      context.rotate(letterAngle);
    }
  }
  context.restore();
}
